/*
 * Copyright © 2020-2024 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2024-01-30 21:52:42
 */
import React, { useRef, useState, useMemo } from 'react'
import { Button, Divider, Layout } from 'antd'
import excel_icon from '../../../../../images/excel_icon.jpeg'
import FileSaver from 'file-saver'
import CONFIG from '../../../../../constants/config'
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks'

import Upload from './Upload'
import Preview from './Preview'
import sensors from '../../../../../utils/sensors'
import ContactUS from '../../../../components/ContactUS'
import { setEditorCascadeItemsList } from '../../../../../actions/watermark'
import {
  CloseOutlined,
  CustomerServiceOutlined,
  DownloadOutlined
} from '@ant-design/icons'

import PageHeader from '@/components/PageHeader'
const { Content } = Layout

// 多级选项数据
interface CascadeData {
  cascade1: string
  cascade2: string
  cascade3: string
}

// 多级选项树节点
interface CascadeNode {
  content: string
  childs?: CascadeNode[]
}

// 多级选项条目
interface CascadeItem {
  itemId: string | number
  records: CascadeNode[]
}

interface BatchImportCascadeItemsProps {
  index: number
  itemID: string | number
  onClose: () => void
}

const BatchImportCascadeItems: React.FC<BatchImportCascadeItemsProps> = (
  props
) => {
  const { index, itemID, onClose } = props

  const [data, setData] = useState<CascadeData[]>([])
  const [isVisibleResults, setIsVisibleResults] = useState(false)
  const dispatch = useAppDispatch()
  const filenameRef = useRef('')
  const watermark = useAppSelector((state) => state.editor.watermark) //watermark.content
  const allCascadeItems = useAppSelector(
    (state) => state.editor.cascadeItems
  ) as CascadeItem[]
  const cascadeItemsIndex = useMemo(() => {
    return allCascadeItems.findIndex((item) => item.itemId === itemID)
  }, [allCascadeItems, itemID])

  const getExcelData = (excelData: CascadeData[]) => {
    setData(excelData)
    setIsVisibleResults(true)
  }

  const handleImportClick = () => {
    const validData = data.filter((item) => {
      if (item.cascade3 && (!item.cascade2 || !item.cascade1)) {
        return false
      }
      if (!item.cascade3 && item.cascade2 && !item.cascade1) {
        return false
      }
      if (item.cascade1) {
        return true
      }
      return false
    })
    // 095 批量导入时，覆盖原有数据
    let newCascadeItems: CascadeNode[] = []
    //将导入的plain数据插入树形结构中
    validData.forEach((item) => {
      //1级选项是否存在，不存在创建
      let cascade1Index = newCascadeItems.findIndex(
        (cascadeItem) => cascadeItem.content === item.cascade1
      )
      if (cascade1Index === -1) {
        newCascadeItems = [
          ...newCascadeItems,
          {
            content: item.cascade1,
            childs: []
          }
        ]
        //1级选项不存在，创建后，1级选项的index为最后一个
        cascade1Index = newCascadeItems.length - 1
      }

      if (item.cascade2) {
        //2级选项是否存在，不存在创建
        const cascade1Childs = newCascadeItems[cascade1Index].childs || []
        let cascade2Index = cascade1Childs.findIndex(
          (cascadeItem) => cascadeItem.content === item.cascade2
        )
        if (cascade2Index === -1) {
          newCascadeItems = newCascadeItems.map((ci, idx) =>
            idx === cascade1Index
              ? {
                  ...ci,
                  childs: [
                    ...(ci.childs || []),
                    {
                      content: item.cascade2,
                      childs: []
                    }
                  ]
                }
              : ci
          )

          //2级选项不存在，创建后，2级选项的index为最后一个
          cascade2Index =
            (newCascadeItems[cascade1Index].childs || []).length - 1
        }
        if (item.cascade3) {
          //3级选项是否存在，不存在创建
          const cascade2Childs =
            newCascadeItems[cascade1Index].childs?.[cascade2Index]?.childs || []
          const cascade3Index = cascade2Childs.findIndex(
            (cascadeItem) => cascadeItem.content === item.cascade3
          )
          if (cascade3Index === -1) {
            newCascadeItems = newCascadeItems.map((ci, idx) =>
              idx === cascade1Index
                ? {
                    ...ci,
                    childs: (ci.childs || []).map((ci2, idx2) =>
                      idx2 === cascade2Index
                        ? {
                            ...ci2,
                            childs: [
                              ...(ci2.childs || []),
                              {
                                content: item.cascade3
                              }
                            ]
                          }
                        : ci2
                    )
                  }
                : ci
            )
          }
        }
      }
    })
    if (cascadeItemsIndex === -1) {
      dispatch(
        setEditorCascadeItemsList([
          ...allCascadeItems,
          {
            itemId: itemID,
            records: newCascadeItems
          }
        ])
      )
    } else {
      dispatch(
        setEditorCascadeItemsList(
          allCascadeItems.map((item, idx) =>
            idx === cascadeItemsIndex
              ? { ...item, records: newCascadeItems }
              : item
          )
        )
      )
    }

    onClose()
  }

  const handleCloseClick = () => {
    reportSaClickEvent('close')
    onClose()
  }

  const reportSaClickEvent = (clickItem: string) => {
    sensors.track('web_click_page_watermark_item_add_multi_upload', {
      clickItem,
      fileName: filenameRef.current,
      token: '',
      pageType: 'groupWatermarkPage',
      fromPlace: 'watermarkMultiOption'
    })
  }

  const reportSaUploadEvent = (content: string) => {
    sensors.track('web_get_action_watermark_item_add_multi_upload', {
      content,
      fileName: filenameRef.current,
      token: '',
      fromPlace: 'watermarkMultiOption'
    })
  }

  return (
    <Layout className="watermark-geo-data">
      <PageHeader
        className="Header"
        title="批量添加选项"
        extra={
          <div className="header-extra">
            <ContactUS fromPage="水印管理-批量添加多级选项">
              <span
                className="service"
                onClick={() => reportSaClickEvent('contactUs')}
              >
                <CustomerServiceOutlined />
                联系客服
              </span>
            </ContactUS>

            <CloseOutlined className="close" onClick={handleCloseClick} />
          </div>
        }
      />
      <Content className="content geo-data-content">
        <div className="batch-import-container">
          <div className="operate-wrapper">
            <div className="step1">
              <div className="title">1.下载模板，填写水印多级选项</div>
              <img src={excel_icon} className="excel-icon" alt="" />
              <div>《多级选项内容模板》</div>
              <Button
                icon={<DownloadOutlined />}
                className="download-template"
                onClick={() => {
                  FileSaver.saveAs(
                    CONFIG.cascadeItemListExcelUrl,
                    '多级选项内容模板.xlsx'
                  )
                  reportSaClickEvent('download')
                }}
              >
                立即下载
              </Button>
            </div>
            <div className="step2">
              <div className="title">2.上传填写完毕的Excel文件</div>
              <Upload
                getExcelData={getExcelData}
                reportSaClickEvent={reportSaClickEvent}
                reportSaUploadEvent={reportSaUploadEvent}
                afterUpload={(filename: string) =>
                  (filenameRef.current = filename)
                }
              />
            </div>
          </div>

          {isVisibleResults && (
            <>
              <Divider className="divider" />
              <Preview data={data} onImportClick={handleImportClick} />
            </>
          )}
        </div>
      </Content>
    </Layout>
  )
}

export default BatchImportCascadeItems
